import { useEffect, useState } from "react";
import { Switch, Route } from "react-router";
import Layout from "@/components/layout/Layout";
import { Toaster } from "@/components/ui/sonner"

import Faq from "./pages/Faq";
import Home from "./pages/site-credentials/Home";
import ContactCenter from "./pages/site-credentials/ContactCenter";
import Authorization from "./pages/site-credentials/Authorization";
import SiteCredentialsTimeOutError from "./pages/site-credentials/Error";
import TermsAndConditions from "./pages/site-credentials/TermsAndConditions";

import { EmailVerifyOtp, SCPasswordRecoverEmailValidateOtp, SCPasswordRecoverPhoneNumberValidateOtp } from "./pages/shared/SharedVerifyOtpPage";

import ActionNotAuthorizedPage from "./pages/site-credentials/ActionNotAuthorized";
import { EmailMfaValidation, PhoneNumberVerifyOtp } from "./pages/shared/SharedVerifyOtpPage";
import PhoneNumberDuplicatedInformation from "./pages/site-credentials/PhoneNumberAssociation/DuplicatedInformation";
import { SCConsentsValidatePassword, SCUpdateEmailValidatePassword, SCUpdatePhoneValidatePassword } from "./pages/shared/SharedValidatePasswordPage";

import { SiteCredentialsSetPassword } from "./pages/shared/SharedSetPasswordPage";
import { SCUpdatePWValidatePassword } from "./pages/shared/SharedValidatePasswordPage";
import SiteCredentialsPasswordRecoverBlocked from "./pages/site-credentials/password-recover/Blocked";
import SCLoyaltyCardOtpOptions from "./pages/site-credentials/LoyaltyCardAssociation/LoyaltyCardOtpOptions";
import SiteCredentialsPasswordRecoverSetPassword from "./pages/site-credentials/password-recover/SetPassword";
import SiteCredentialsPasswordRecoverContactCenter from "./pages/site-credentials/password-recover/ContactCenter";
import AboutAssociateLoyaltyCard from "./pages/site-credentials/LoyaltyCardAssociation/AboutAssociateLoyaltyCard";
import SCLoyaltyCardAssociationSuccess from "./pages/site-credentials/LoyaltyCardAssociation/LoyaltyCardAssociationSuccess";
import SCLoyaltyCardContactCenter from "./pages/site-credentials/LoyaltyCardAssociation/LoyaltyCardContactCenter";
import SCLoyaltyCardOtpValidation from "./pages/site-credentials/LoyaltyCardAssociation/LoyaltyCardOtpValidation";
import { SCLoyaltyCardAssociation, SCPasswordRecoverLoyaltyCardAssociation } from "./pages/shared/loyaltyCard/LoyaltyCardAssociation";

import ConsentListPage from "./pages/site-credentials/Consents/ConsentListPage";

import {
  DigitalAssetContext,
  defaultDigitalAsset,
} from "./contexts/site-credentials/DigitalAssetContext";

import { DigitalAsset } from "./models/site-credentials/common/DigitalAsset";
import { SCPhoneDefinition, SCEmailDefinition } from "./pages/shared/SharedAssociateIdentifierPage";
import SharedAccountAssociationOptions from "./pages/shared/SharedAccountAssociationOptions";
import LoyaltyCardCreation from "./pages/continente-credentials/loyaltycard/LoyaltyCardCreation";
import IdentificationVerification from "./pages/continente-credentials/loyaltycard/IdentificationVerification";
import SuccessPage from "./pages/shared/SharedSuccessPage";
import AuthorizationHandsoff from "./pages/site-credentials/AuthorizationHandsoff";
import { initializeGA4 } from "@/lib/ga-loader";

const { fetch: originalFetch } = window;

const cookies: Record<string, string> = {};

async function fetch(
  input: RequestInfo | URL,
  init: RequestInit = {}
): Promise<Response> {
  const cookieNames = Object.keys(cookies);

  if (cookieNames.length > 0) {
    const cookieHeader = cookieNames
      .map((c) => `${c}=${cookies[c]}`)
      .join("; ");

    init.headers = {
      ...init.headers,
      "X-Cookie": cookieHeader,
    };
  }

  const resource: RequestInfo = input instanceof URL ? input.toString() : input;

  const response = await originalFetch(resource, init);

  const setCookieHeader = response.headers.get("X-Set-Cookie");

  if (setCookieHeader) {
    const parts = setCookieHeader.split("=");

    if (parts.length >= 2) {
      cookies[parts[0]] = parts[1].split(";")[0];
    }
  }

  return response;
}

window.fetch = fetch;

export default function SiteCredenciaisApp() {
  const [digitalAsset, setDigitalAsset] =
    useState<DigitalAsset>(defaultDigitalAsset);

  const updateDigitalAsset = (newDigitalAsset: DigitalAsset) => {
    setDigitalAsset(newDigitalAsset);
  };

  useEffect(() => {
    if (digitalAsset.googleAnalyticsEnabled) {
      initializeGA4();
    }
  }, [digitalAsset.googleAnalyticsEnabled]);

  return (
    <DigitalAssetContext.Provider
      value={{
        digitalAsset: digitalAsset,
        updateDigitalAsset: updateDigitalAsset,
      }}
    >
      <Layout className="self-center max-w-[40ch] w-full px-8 mx-auto">
        <Toaster />
        <Route
          exact path="/site-credentials"
          component={Home} // CHECKED
        />
        <Route
          exact path="/site-credentials/authorize"
          component={Authorization} // CHECKED 
        />
        <Route
          exact path="/site-credentials/authorize/:key"
          component={AuthorizationHandsoff}
        />
        <Route
          path="/site-credentials/success"
          component={SuccessPage} // CHECKED // SHARED
        />
        <Route
          path="/site-credentials/email/definition"
          component={SCEmailDefinition} // CHECKED // SHARED
        />
        <Route
          path="/site-credentials/email/validate-password"
          component={SCUpdateEmailValidatePassword} // CHECKED // SHARED
        />
        <Route
          path="/site-credentials/email/otp-validation"
          component={EmailVerifyOtp} // CHECKED // SHARED
        />
        <Route
          path="/site-credentials/email/loyalty-card-options"
          component={SharedAccountAssociationOptions} // CHECKED // SHARED
        />
        <Route
          path="/site-credentials/phonenumber/definition"
          component={SCPhoneDefinition} // CHECKED // SHARED
        />
        <Route
          path="/site-credentials/phonenumber/validate-password"
          component={SCUpdatePhoneValidatePassword} // CHECKED // SHARED
        />
        <Route
          path="/site-credentials/phonenumber/otp-validation"
          component={PhoneNumberVerifyOtp} // CHECKED // SHARED
        />
        <Route
          path="/site-credentials/phonenumber/duplicated-information"
          component={PhoneNumberDuplicatedInformation} // CHECKED
        />
        <Route
          path="/site-credentials/phonenumber/mfa-validation"
          component={EmailMfaValidation} // CHECKED // SHARED
        />
        <Route
          path="/site-credentials/loyaltycard/about"
          component={AboutAssociateLoyaltyCard} // CHECKED
        />
        <Route
          path="/site-credentials/card/association"
          component={SCLoyaltyCardAssociation}  // CHECKED // SHARED
        />
        <Route
          path="/site-credentials/password/update/validate"
          component={SCUpdatePWValidatePassword} // CHECKED // SHARED
        />
        <Route
          path="/site-credentials/password/update/definition"
          component={SiteCredentialsSetPassword} // CHECKED // SHARED
        />
        <Route
          path="/site-credentials/password/definition/password-definition"
          component={SiteCredentialsSetPassword} // CHECKED // SHARED
        />
        <Route
          path="/site-credentials/card/identifier-verification"
          component={IdentificationVerification} // CHECKED // SHARED
        />
        <Route
          path="/site-credentials/card/loyalty-card-association-success"
          component={SCLoyaltyCardAssociationSuccess} // CHECKED
        />
        <Route
          path="/site-credentials/loyalty/cards/otp-options"
          component={SCLoyaltyCardOtpOptions} // CHECKED
        />
        <Route
          path="/site-credentials/loyalty/cards/verify-otp"
          component={SCLoyaltyCardOtpValidation}
        />
        <Route
          path="/site-credentials/card/continente-card-creation"
          component={LoyaltyCardCreation} // CHECKED // SHARED
        />
        <Route
          path="/site-credentials/card/call-center-loyalty-card"
          component={SCLoyaltyCardContactCenter} // CHECKED 
        />
        <Route
          path="/site-credentials/contact-center"
          component={ContactCenter} // CHECKED 
        />
        <Route
          path="/site-credentials/faq"
          component={Faq} // CHECKED
        />
        <Route
          path="/site-credentials/termsandconditions"
          component={TermsAndConditions} // CHECKED
        />
        <Route
          path="/site-credentials/error"
          component={SiteCredentialsTimeOutError} // CHECKED
        />
        <Route
          path="/site-credentials/action-not-authorized"
          component={ActionNotAuthorizedPage}
        />

        <Route
          path="/site-credentials/password/recover/validate-otp"
          component={SCPasswordRecoverPhoneNumberValidateOtp} // CHECKED // SHARED
        />
        <Route
          path="/site-credentials/password/recover/validate-otp-email"
          component={SCPasswordRecoverEmailValidateOtp} // CHECKED // SHARED
        />
        <Route
          path="/site-credentials/password/recover/set-password"
          component={SiteCredentialsPasswordRecoverSetPassword} // CHECKED // SHARED
        />
        <Route
          path="/site-credentials/password/recover/loyalty-card-validation"
          component={SCPasswordRecoverLoyaltyCardAssociation} // CHECKED // SHARED}
        />
        <Route
          path="/site-credentials/password/recover/contact-center"
          component={SiteCredentialsPasswordRecoverContactCenter} // CHECKED
        />
        <Route
          path="/site-credentials/password/recover/blocked"
          component={SiteCredentialsPasswordRecoverBlocked} // CHECKED
        />
        <Route
          exact path="/site-credentials/consents/validate-password"
          component={SCConsentsValidatePassword}
        />
        <Route
          exact path="/site-credentials/consents"
          component={ConsentListPage}
        />
      </Layout>
    </DigitalAssetContext.Provider >
  );
}